import type {
  BookingInfo,
  PaymentState,
  Response,
  ServiceGender,
} from "~/types";
import type { BusinessResponse, AboutBusinessResponse } from "~/types/business";

interface FinishResponse {
  title: string;
  description: string;
  payment_state: PaymentState;
  can_retry_payment: boolean;
  current_payment_method_id: number | null;
  booking: BookingInfo;
}

export const useBusiness = () => {
  const { apiVersion } = useRuntimeConfig().public;

  const getBusiness = async (location_identifier: string) => {
    const { data, error } = await useLarafetch(
      `/api/${apiVersion}/${location_identifier}`
    );

    /**
     * `useFetch` reports a failure on `error` rather than rejecting. Dropping
     * it and handing back an empty `data` left callers unable to tell a
     * storefront that does not exist from one the API could not answer for —
     * so a throttle or a 5xx reached the visitor as "page not found".
     */
    if (error.value) {
      throw error.value;
    }

    return data?.value as Response<BusinessResponse>;
  };

  const bookingFinished = async (
    location_identifier: string,
    reference: string
  ) => {
    const { data } = await useLarafetch(
      `/api/${apiVersion}/${location_identifier}/b-${reference}/finished`
    );
    return data.value as Response<FinishResponse>;
  };

  const refetchBookingFinished = async (
    location_identifier: string,
    reference: string
  ) =>
    (await $larafetch(
      `/api/${apiVersion}/${location_identifier}/b-${reference}/finished`
    )) as Response<FinishResponse>;

  const getAboutUs = async (
    location_identifier: string,
    query: Record<string, string> = {}
  ) => {
    const { data, error } = await useLarafetch(
      `/api/${apiVersion}/${location_identifier}/about-us`,
      { query }
    );

    // Same reason as `getBusiness`: `useFetch` puts failures on `error`, and
    // swallowing it leaves the caller with an empty `data` it cannot tell
    // apart from a storefront that does not exist.
    if (error.value) {
      throw error.value;
    }

    return data.value as Response<AboutBusinessResponse>;
  };

  const getLocationEmployees = async (
    location_identifier: string,
    selectedServices: { id: number | string; service_type?: string }[],
    options: { matchAll?: boolean; bookingDate?: string; gender?: ServiceGender | null  } = {}
  ) => {
    const services: (number | string)[] = [];
    const packages: (number | string)[] = [];
    selectedServices.forEach((service) => {
      if (service.service_type === "package") packages.push(service.id);
      else services.push(service.id);
    });
    return await $larafetch(
      `/api/${apiVersion}/${location_identifier}/employees`,
      {
        method: "get",
        query: {
          services: services.join(","),
          packages: packages.join(","),
          ...(options.matchAll ? { match: "all" } : {}),
          ...(options.bookingDate ? { booking_date: options.bookingDate } : {}),
          ...(options.gender ? { gender: options.gender } : {}),
        },
      }
    );
  };

  return {
    getBusiness,
    bookingFinished,
    refetchBookingFinished,
    getAboutUs,
    getLocationEmployees,
  };
};
